import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ShopTypes } from '~/lib/data-contract';
import { BasketValidationError } from '~/lib/data-contract/auto-generated/shop-api-model';
import {
    useAddBasketItem,
    useBasketReference,
    usePrice,
    useProduct,
    useStockDetails,
    useTranslations,
    useUserProfile,
} from '~/services';
import { ErrorMessage, Form } from '~/shared/components/Form';
import {
    SelectAmount,
    SelectAmountForm,
    SelectAmountTypeFormFields,
} from '~/shared/components/SelectAmount';
import gtm from '~/shared/tracking/gtm';
import { getGAItem } from '~/shared/tracking/gtm/utils/getGAItem';
import { FreightTypes, useFreightType, usePostalCode } from '~/shared/utils/storage';
import {
    closeModalFreight,
    errorMessages,
    openModalFreight,
    openQuickAddModal,
    resetQuickAdd,
    setActiveSetWarehouseId,
    setItemNo,
} from '~/state/useModalFreight';
import useModalState from '~/state/useModalState';
import useQuickModalState, {
    resetBasketModalID,
    setQuickFreightModalId,
    setQuickFreightSuccessCallback,
} from '~/state/useQuickAddToBasketState';
import { AddToBasketWrapper } from '../styled';
import { ButtonWrapper, BuyButton, ErrorMessages } from './styled';

type Props = {
    className?: string;
    itemNo: string;
    activeWarehouseId: number | undefined;
    callbackHandler: (callback?: (args?: unknown) => void) => void;
    freightPriceHandler: (itemNo: string) => void;
    shouldShowPowerStep?: boolean;
    modalType?: 'quickAdd' | 'freight';
    productStatus: string | undefined;
    productIsOutOfStock?: boolean;
};

export default function AddToBasket({
    itemNo,
    activeWarehouseId,
    className,
    callbackHandler,
    freightPriceHandler,
    shouldShowPowerStep = false,
    productStatus,
    modalType,
    productIsOutOfStock,
}: Props) {
    const { mutate: addBasketItem, isLoading, error } = useAddBasketItem(shouldShowPowerStep);
    const { data: basketReference } = useBasketReference();
    const { data } = useSession();
    const { data: profile } = useUserProfile();
    const token = data?.accessToken ?? null;
    const customerId = profile?.customer?.id ?? '';
    const { translate } = useTranslations();
    const [postalCode] = usePostalCode();
    const [freightType, setFreightType] = useFreightType();
    const { setFreightPriceSuccessCallback } = useModalState();

    const { data: stockDetails } = useStockDetails({ itemNo, postalCode });
    const [missingFreightTypeIsVisible, setMissingFreightTypeIsVisible] = useState(false);

    const { data: productData } = useProduct(itemNo, ({ product }) => product);

    const { data: priceData } = usePrice(
        itemNo,
        productData?.item?.itemStatus !== ShopTypes.ItemStatusValues.Obsolete,
        customerId,
        token,
    );

    const warehouses = stockDetails?.pickup?.warehouses;
    const itemsInStock = warehouses?.filter(
        (warehouse) => warehouse.status === 'InStock' || warehouse.status === 'LowStock',
    ).length;

    const { itemListName } = useQuickModalState((state) => ({
        itemListName: state.itemList,
    }));
    const methods = useForm<SelectAmountForm>({
        defaultValues: {
            [SelectAmountTypeFormFields.Quantity]: 1,
        },
    });

    const onSubmit = (formData: SelectAmountForm) => {
        setMissingFreightTypeIsVisible(false);

        if (isLoading) {
            return;
        }

        const isMissingFreightType = !freightType;
        if (isMissingFreightType) {
            setMissingFreightTypeIsVisible(true);
            return;
        }

        const isMissingWarehouse = freightType === FreightTypes.Pickup && !activeWarehouseId;

        if (isMissingWarehouse) {
            // TODO: Find better way to handle missing warehouse selection error
            console.warn('AddToBasket: Cannot add to basket. Warehouse not selected.');
            return;
        }

        const addBasketItemCallback = () =>
            addBasketItem(
                {
                    itemNo,
                    postalCode,
                    quantity: formData[SelectAmountTypeFormFields.Quantity],
                    warehouseId:
                        freightType === FreightTypes.Pickup ? activeWarehouseId : undefined,
                    isBundle: !!productData?.isBundle,
                },
                {
                    onSuccess: () => {
                        methods?.resetField(SelectAmountTypeFormFields.Quantity);

                        const quantity = formData[SelectAmountTypeFormFields.Quantity];
                        const price = priceData?.price;
                        const gaItem = getGAItem(
                            quantity,
                            productData,
                            price,
                            priceData?.activeCampaigns,
                            itemListName,
                        );

                        if (gaItem)
                            gtm.ecommerce.addToCart(
                                (price?.price.priceInclVat ?? 0) * quantity,
                                [gaItem],
                                basketReference,
                            );

                        resetBasketModalID();
                        setActiveSetWarehouseId(undefined);
                        setItemNo('');
                        resetQuickAdd();
                        closeModalFreight();
                    },

                    onSettled: () => {
                        setFreightPriceSuccessCallback();
                        setQuickFreightSuccessCallback?.();
                    },
                },
            );

        const isMissingFreightPostalCode =
            freightType === FreightTypes.Online &&
            stockDetails?.online?.requiresPostalCode &&
            !postalCode;

        if (isMissingFreightPostalCode) {
            callbackHandler(addBasketItemCallback);
            setQuickFreightModalId(itemNo);
            freightPriceHandler(itemNo);
            return;
        }

        addBasketItemCallback();
    };

    const validationError = error as BasketValidationError;

    useEffect(() => {
        if (validationError) {
            errorMessages(
                translate(
                    validationError?.validationText?.name,
                    validationError?.validationText?.values,
                ),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validationError]);

    const modalHandler = () => {
        switch (modalType) {
            case 'quickAdd':
                openQuickAddModal();
                break;
            case 'freight':
                openModalFreight();
                break;
            default:
                console.error(`Invalid modal type: ${modalType}`);
                break;
        }
    };
    return (
        <ButtonWrapper>
            <Form id={modalType} className={className} methods={methods} onSubmit={onSubmit}>
                <AddToBasketWrapper>
                    <SelectAmount disabled={isLoading} name={SelectAmountTypeFormFields.Quantity} />

                    <BuyButton
                        disabled={productIsOutOfStock || !stockDetails?.online}
                        isLoading={isLoading && freightType === FreightTypes.Online}
                        onClick={() => {
                            setFreightType(FreightTypes.Online);
                            setActiveSetWarehouseId(undefined);
                        }}
                        type="submit"
                    >
                        {translate('generic.buyOnline')}
                    </BuyButton>
                </AddToBasketWrapper>
            </Form>

            <BuyButton
                variant="stroke"
                onClick={() => {
                    setItemNo(itemNo);
                    modalHandler();
                }}
                disabled={
                    !stockDetails?.pickup ||
                    productStatus === 'Obsolete' ||
                    itemsInStock === 0 ||
                    !itemNo
                }
            >
                {translate('generic.pickupAtStore')}
            </BuyButton>
            <ErrorMessages>
                {validationError && freightType === FreightTypes.Online && (
                    <ErrorMessage>
                        {translate(
                            validationError?.validationText?.name,
                            validationError?.validationText?.values,
                        )}
                    </ErrorMessage>
                )}
                {missingFreightTypeIsVisible && (
                    <ErrorMessage>
                        {translate('product.freightTypeMissingErrorMessage')}
                    </ErrorMessage>
                )}
            </ErrorMessages>
        </ButtonWrapper>
    );
}
